import React, { useMemo, useState } from 'react';
import { thumbUrl } from './metaApiSource';

const euros = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  if (n >= 1000) return '€' + (n / 1000).toFixed(1).replace('.0', '') + 'k';
  return '€' + Math.round(n);
};

function shortCreator(ad) {
  const raw = ad.nomenclature?.creator || (ad.ad_name_sample || '').split('_')[0] || '—';
  return raw.replace(/_/g, ' ');
}

// ── Period grouping helpers ────────────────────────────────────────────────
function isoWeekKey(dstr) {
  if (!dstr) return null;
  const d = new Date(dstr + 'T00:00:00Z');
  if (isNaN(d)) return null;
  const dt = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() + 4 - day);
  const yStart = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  const wk = Math.ceil((((dt - yStart) / 86400000) + 1) / 7);
  return dt.getUTCFullYear() + '-W' + String(wk).padStart(2, '0');
}
function monthKey(dstr) {
  if (!dstr || dstr.length < 7) return null;
  return dstr.slice(0, 7);
}
function weekLabel(key) {
  const [y, w] = key.split('-W');
  const year = parseInt(y, 10), wk = parseInt(w, 10);
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const mon = new Date(jan4);
  mon.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() || 7) - 1) + (wk - 1) * 7);
  const sun = new Date(mon); sun.setUTCDate(mon.getUTCDate() + 6);
  const f = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return 'Week ' + wk + ' · ' + f(mon) + ' – ' + f(sun);
}
function monthLabel(key) {
  const [y, m] = key.split('-');
  return new Date(Date.UTC(parseInt(y, 10), parseInt(m, 10) - 1, 1))
    .toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function WeeklyAdRow({ ad, onOpenCreative, onPlay }) {
  return (
    <div
      className="perf-weekly-row"
      onClick={onOpenCreative ? () => onOpenCreative(ad) : undefined}
      style={onOpenCreative ? { cursor: 'pointer' } : undefined}>
      <div onClick={(e) => { if (onPlay && ad.ad_id) { e.stopPropagation(); onPlay(ad.ad_id); } }} style={{ width: 38, height: 38, borderRadius: 6, overflow: 'hidden', flexShrink: 0, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 10, cursor: ad.ad_id ? 'pointer' : 'default', position: 'relative' }}>
        {thumbUrl(ad)
          ? <img src={thumbUrl(ad)} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <span style={{ color: '#bbb', fontSize: 12 }}>▶</span>}
      </div>
      <div className="perf-weekly-row-name">
        <div className="perf-weekly-row-creator">{shortCreator(ad)}</div>
        <div className="perf-weekly-row-id">#{ad.nomenclature?.metaSeqId || ad.core_id || '—'}</div>
      </div>
      <div className="perf-weekly-row-stats">
        <span className="perf-weekly-stat">{ad.achats ?? 0} <small>purchases</small></span>
        <span className="perf-weekly-stat">{ad.cpa !== null && ad.cpa !== undefined ? '€' + Math.round(ad.cpa) : '—'} <small>CPA</small></span>
        <span className="perf-weekly-stat">{euros(ad.spend)} <small>spend</small></span>
      </div>
    </div>
  );
}

function WeeklyAdCard({ ad, onOpenCreative, onPlay }) {
  const t = thumbUrl(ad);
  return (
    <div style={{ border: '1px solid #eee', borderRadius: 11, overflow: 'hidden', background: '#fff' }}>
      <div onClick={() => { if (onPlay && ad.ad_id) onPlay(ad.ad_id); }} style={{ position: 'relative', aspectRatio: '9 / 16', background: '#f0f0f0', cursor: ad.ad_id ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {t
          ? <img src={t} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <span style={{ color: '#bbb', fontSize: 26 }}>▶</span>}
        {t && <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 34, height: 34, borderRadius: '50%', background: 'rgba(0,0,0,0.45)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, pointerEvents: 'none' }}>▶</span>}
      </div>
      <div onClick={() => onOpenCreative && onOpenCreative(ad)} style={{ padding: '8px 10px', cursor: onOpenCreative ? 'pointer' : 'default' }}>
        <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={ad.ad_name_sample}>{shortCreator(ad)}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 7, fontSize: 11 }}>
          <span><b>{ad.achats ?? 0}</b> <span style={{ color: '#999' }}>ach.</span></span>
          <span><b>{ad.cpa !== null && ad.cpa !== undefined ? '€' + Math.round(ad.cpa) : '—'}</b> <span style={{ color: '#999' }}>CPA</span></span>
          <span><b>{euros(ad.spend)}</b></span>
        </div>
      </div>
    </div>
  );
}

export default function WeeklyTestsBlock({ enrichedAds, onOpenCreative, onPlay, groupBy = 'week', viewMode = 'list' }) {
  const worked = useMemo(() => {
    const testing = (enrichedAds || []).filter(c => c.phase === 'testing');
    const w = testing.filter(ad => ad.action === 'PROMOTE');
    w.sort((a, b) => (b.achats || 0) - (a.achats || 0));
    return w;
  }, [enrichedAds]);

  const groups = useMemo(() => {
    const keyFn = groupBy === 'month' ? monthKey : isoWeekKey;
    const labelFn = groupBy === 'month' ? monthLabel : weekLabel;
    const map = new Map();
    for (const ad of worked) {
      const key = keyFn(ad.last_date || ad.first_date) || 'undated';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ad);
    }
    return Array.from(map.entries())
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([key, ads]) => ({ key, label: key === 'undated' ? 'Undated' : labelFn(key), ads }));
  }, [worked, groupBy]);

  const [openKeys, setOpenKeys] = useState(() => new Set()); // all weeks closed by default
  const isOpen = (idx, key) => openKeys.has(key);
  const toggle = (key) => setOpenKeys(prev => {
    const n = new Set(prev);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });

  if (worked.length === 0) {
    return <div className="perf-weekly-empty">No winners yet in this period</div>;
  }
  return (
    <div className="perf-weekly-list">
      {groups.map((g, idx) => {
        const open = isOpen(idx, g.key);
        return (
          <div key={g.key}>
            <div onClick={() => toggle(g.key)} style={{ padding: '10px 4px 6px', fontSize: 11, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#bbb', fontSize: 10, display: 'inline-block', transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform .15s' }}>▾</span>
                {g.label}
              </span>
              <span style={{ color: '#aaa', fontWeight: 600 }}>{g.ads.length}</span>
            </div>
            {open && (viewMode === 'grid' ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12, padding: '2px 0 10px' }}>
                {g.ads.map(ad => (
                  <WeeklyAdCard key={ad.core_id || ad.ad_name_sample} ad={ad} onOpenCreative={onOpenCreative} onPlay={onPlay} />
                ))}
              </div>
            ) : (
              g.ads.map(ad => (
                <WeeklyAdRow key={ad.core_id || ad.ad_name_sample} ad={ad} onOpenCreative={onOpenCreative} onPlay={onPlay} />
              ))
            ))}
          </div>
        );
      })}
    </div>
  );
}
